import {APP_INITIALIZER, InjectionToken, Provider} from '@angular/core';
import {PAYMENT_FEEDBACK} from '@features/payment/domain/ports/ui/feedback.port';
import {
  PAYMENT_CHECKOUT_RECOVERY,
  PAYMENT_VERIFICATION_RECOVERY
} from '@features/payment/domain/ports/ui/recovery.port';
import {CheckoutRecoveryHandler} from '@features/payment/infrastructure/ui/checkout-recovery.handler';
import {VerificationRecoveryHandler} from '@features/payment/infrastructure/ui/verification-recovery.handler';
import {PAYMENT_ROUTE} from '@features/payment/domain/ports/navigation/route.port';
import {PAYMENT_REDIRECT} from '@features/payment/domain/ports/navigation/redirect.port';
import {RefundNavigator} from '@features/payment/infrastructure/ui/refund-navigator';
import {PaymentFeedbackPresenter} from '@features/payment/infrastructure/ui/payment-feedback.presenter';
import {RefundRouteHandler} from '@features/payment/infrastructure/ui/refund-route.handler';
import {PAYMENT_STRATEGY_REGISTRY} from '@features/payment/domain/ports/strategy/payment-strategy.port';
import {REFUND_STRATEGY_REGISTRY} from '@features/payment/domain/ports/strategy/refund-strategy.port';
import {CASH_AMOUNT_PROMPT} from '@features/payment/domain/ports/ui/cash-amount-prompt.port';
import {CashAmountPromptHandler} from '@features/payment/infrastructure/ui/cash-amount-prompt.handler';
import {PAYMENT_SETTINGS} from '@features/payment/domain/ports/ui/payment-settings.port';
import {PaymentSettingsHandler} from '@features/payment/infrastructure/ui/payment-settings.handler';
import {
  InfrastructurePaymentStrategyRegistry
} from '@features/payment/infrastructure/registries/payment-strategy.registry';
import {
  InfrastructureRefundStrategyRegistry
} from '@features/payment/infrastructure/registries/refund-strategy.registry';
import {StoreIndexedDbStorage} from '@platform/storage/infrastructure/storage-indexdb.storage';
import {StorageSynchronizer} from '@platform/storage/application/storage.synchronizer';
import {PaymentChargeStore, PaymentChargeState} from '@features/payment/application/stores/charge/payment-charge.store';
import {Logger} from 'src/app/core/interfaces/Logger';
import {
  PaymentOnOrderUnpaidSubscriber
} from '@features/payment/application/subscribers/payment-on-order-unpaid.subscriber';
import {
  PaymentOnServiceClosedSubscriber
} from '@features/payment/application/subscribers/payment-on-service-closed.subscriber';
import {PAYMENT_GATEWAY_RESOLVER} from '@features/payment/domain/ports/resolver/payment-gateway-resolver.port';
import {DefaultPaymentGatewayResolver} from '@features/payment/infrastructure/resolvers/payment-gateway.resolver';

export const DOMAIN_SUBSCRIBERS = new InjectionToken('DOMAIN_SUBSCRIBERS');
export const PAYMENT_STORE_STORAGE = new InjectionToken('PAYMENT_STORE_STORAGE');

export const PAYMENT_PROVIDERS: Provider[] = [
  {
    provide: PAYMENT_FEEDBACK,
    useClass: PaymentFeedbackPresenter,
  },
  {
    provide: PAYMENT_CHECKOUT_RECOVERY,
    useClass: CheckoutRecoveryHandler,
  },
  {
    provide: PAYMENT_VERIFICATION_RECOVERY,
    useClass: VerificationRecoveryHandler,
  },
  {
    provide: PAYMENT_ROUTE,
    useClass: RefundRouteHandler,
  },
  {
    provide: PAYMENT_REDIRECT,
    useClass: RefundNavigator,
  },
  {
    provide: PAYMENT_STRATEGY_REGISTRY,
    useClass: InfrastructurePaymentStrategyRegistry,
  },
  {
    provide: PAYMENT_GATEWAY_RESOLVER,
    useClass: DefaultPaymentGatewayResolver,
  },
  {
    provide: CASH_AMOUNT_PROMPT,
    useClass: CashAmountPromptHandler,
  },
  {
    provide: PAYMENT_SETTINGS,
    useClass: PaymentSettingsHandler,
  },
  {
    provide: REFUND_STRATEGY_REGISTRY,
    useClass: InfrastructureRefundStrategyRegistry,
  },
  {
    provide: PAYMENT_STORE_STORAGE,
    useFactory: (l: Logger) => new StoreIndexedDbStorage<PaymentChargeState>(l, 'payment'),
    deps: [Logger],
  },
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (s: StoreIndexedDbStorage<PaymentChargeState>, store: PaymentChargeStore) => new StorageSynchronizer(s, store).sync(),
    deps: [PAYMENT_STORE_STORAGE, PaymentChargeStore],
  },
  {
    provide: DOMAIN_SUBSCRIBERS,
    useClass: PaymentOnOrderUnpaidSubscriber,
    multi: true,
  },
  {
    provide: DOMAIN_SUBSCRIBERS,
    useClass: PaymentOnServiceClosedSubscriber,
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    multi: true,
    deps: [DOMAIN_SUBSCRIBERS],
    useFactory: (s: unknown[]) => () => void s,
  },
];
